import React from 'react';
import { FiTrendingUp, FiTarget, FiLayers } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';

const About = () => {
  const { isDark } = useDarkMode();

  const features = [
    {
      icon: FiTrendingUp,
      title: 'Strategic Growth',
      description: 'Data-driven strategies to scale your brand presence.',
    },
    {
      icon: FiTarget,
      title: 'Influence & Authority',
      description: 'Build thought leadership and brand credibility.',
    },
    {
      icon: FiLayers,
      title: 'Smart Strategy',
      description: 'Creative campaigns that turn engagement into conversions.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const featureVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    }),
  };

  const statVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.6,
      },
    }),
  };

  return (
    <section
      id="about"
      className={`relative w-full py-20 lg:py-32 transition-colors duration-500 ${
        isDark ? 'bg-dark-primary' : 'bg-light-secondary'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Background Decorations */}
        <motion.div
          className={`absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl -z-10 ${
            isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/8'
          }`}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 6, repeat: Infinity }}
        ></motion.div>

        <motion.div
          className={`absolute bottom-0 left-0 w-80 h-80 rounded-full blur-3xl -z-10 ${
            isDark ? 'bg-primary-gold/3' : 'bg-primary-gold/5'
          }`}
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 7, repeat: Infinity, delay: 1 }}
        ></motion.div>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          {/* Left Content */}
          <motion.div className="space-y-6" variants={itemVariants}>
            <div className="space-y-2">
              <motion.p
                className="text-primary-gold font-montserrat font-semibold uppercase tracking-widest text-sm"
                animate={{ letterSpacing: [0, '0.1em', 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                About Us
              </motion.p>
              <motion.h2
                className={`font-montserrat font-bold text-4xl lg:text-5xl transition-colors ${
                  isDark ? 'text-white' : 'text-black'
                }`}
                variants={itemVariants}
              >
                Who We Are
              </motion.h2>
            </div>

            <motion.p
              className={`font-poppins text-lg leading-relaxed transition-colors ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
              variants={itemVariants}
            >
              Media Society is a premium social media agency specializing in Instagram
              management and influencer campaigns. We help brands and restaurants grow,
              engage, and convert—transforming social presence into business results.
            </motion.p>

            <motion.p
              className={`font-poppins text-base leading-relaxed transition-colors ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}
              variants={itemVariants}
            >
              Since our founding, we've partnered with industry leaders to craft compelling
              narratives, manage communities, and execute campaigns that drive measurable
              growth. Your success is our mission.
            </motion.p>

            {/* CTA */}
            <motion.div className="pt-6" variants={itemVariants}>
              <motion.button
                  onClick={() => {
                    const workDetails = document.getElementById('our-work-details');
                    if (workDetails) {
                      workDetails.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      const servicesSection = document.getElementById('services');
                      servicesSection?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                className={`px-8 py-3 bg-primary-gold text-black font-montserrat font-bold rounded-lg transition-all duration-300 cursor-pointer ${
                  isDark
                    ? 'hover:shadow-gold-glow-lg'
                    : 'hover:shadow-lg'
                }`}
                whileHover={{
                  scale: 1.05,
                  y: -3,
                }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Right Features */}
          <motion.div
            className="space-y-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: '-100px' }}
          >
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  className={`flex gap-4 p-6 rounded-xl border transition-all duration-300 group ${
                    isDark
                      ? 'bg-dark-secondary border-primary-gold/10 hover:border-primary-gold/30 hover:bg-dark-tertiary'
                      : 'bg-light-tertiary border-primary-gold/20 hover:border-primary-gold/40 hover:bg-gray-200'
                  }`}
                  variants={featureVariants}
                  custom={index}
                  whileHover={{
                    x: 10,
                    y: -5,
                  }}
                >
                  <motion.div
                    className="flex-shrink-0"
                    animate={{
                      y: [0, -5, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: index * 0.2,
                    }}
                  >
                    <motion.div
                      className={`flex items-center justify-center h-12 w-12 rounded-lg transition-all ${
                        isDark
                          ? 'bg-primary-gold/10 group-hover:bg-primary-gold/20'
                          : 'bg-primary-gold/15 group-hover:bg-primary-gold/25'
                      }`}
                      whileHover={{
                        scale: 1.15,
                        rotate: 10,
                      }}
                    >
                      <IconComponent className="h-6 w-6 text-primary-gold" />
                    </motion.div>
                  </motion.div>
                  <motion.div className="flex-1">
                    <motion.h3
                      className={`font-montserrat font-bold mb-2 transition-colors ${
                        isDark ? 'text-white' : 'text-black'
                      }`}
                      whileHover={{ color: '#FFD700' }}
                    >
                      {feature.title}
                    </motion.h3>
                    <p
                      className={`font-poppins text-sm transition-colors ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      {feature.description}
                    </p>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          className={`mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 pt-12 border-t transition-colors ${
            isDark ? 'border-primary-gold/10' : 'border-primary-gold/20'
          }`}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          {[
            { number: '50+', label: 'Team Members' },
            { number: '100+', label: 'Satisfied Clients' },
            { number: '2M+', label: 'Combined Followers' },
            { number: '10+', label: 'Years Experience' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="text-center group cursor-pointer"
              variants={statVariants}
              custom={index}
              whileHover={{
                scale: 1.15,
                y: -8,
              }}
            >
              <motion.p
                className="font-montserrat font-bold text-3xl lg:text-4xl text-primary-gold"
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  delay: index * 0.2,
                }}
              >
                {stat.number}
              </motion.p>
              <motion.p
                className={`font-poppins mt-2 text-sm lg:text-base transition-colors ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                whileHover={{ color: '#FFD700' }}
              >
                {stat.label}
              </motion.p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
